import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Image as ImageIcon, Upload, Trash2, Plus, Clock, Calendar,
  Save, GripVertical, Loader2, AlertCircle,
} from "lucide-react";
import { compressImage } from "@/utils/imageCompression";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["BRANDING", "DISEÑO GRÁFICO", "IMPRESIÓN", "REDES SOCIALES"];

// Resolve any stored image reference to a browser-safe URL for preview.
const resolveImg = (u) => {
  if (!u) return "";
  if (u.startsWith("data:") || u.startsWith("http") || u.startsWith("/assets/") || u.startsWith("blob:")) return u;
  if (u.startsWith("/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return `${process.env.REACT_APP_BACKEND_URL}/${u}`;
};

const LandingContentManager = () => {
  const [settings, setSettings] = useState({
    portfolio_items: [],
    print_items: [],
    call_schedule: { enabled: true, start: "08:00", end: "21:00", timezone: "America/New_York", holidays: [] },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [newHoliday, setNewHoliday] = useState("");
  const fileInputs = useRef({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/public/landing-settings`);
      const d = r.data || {};
      setSettings({
        portfolio_items: d.portfolio_items || [],
        print_items: d.print_items || [],
        call_schedule: d.call_schedule || { enabled: true, start: "08:00", end: "21:00", timezone: "America/New_York", holidays: [] },
      });
    } catch (e) {
      console.error("Error loading settings:", e);
      toast.error("Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  };

  const saveAll = async (patch) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const body = patch || settings;
      const r = await axios.put(`${API}/admin/landing-settings`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = r.data || {};
      setSettings({
        portfolio_items: d.portfolio_items || [],
        print_items: d.print_items || [],
        call_schedule: d.call_schedule || settings.call_schedule,
      });
      toast.success("Configuración guardada");
    } catch (e) {
      console.error("Save error:", e);
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Selecciona una imagen válida");
      return null;
    }
    // Compress client-side and return base64 data URL (stored in MongoDB, persistent across deploys)
    const { dataUrl, sizeKB } = await compressImage(file, { maxWidth: 1400, maxHeight: 1400, quality: 0.8 });
    if (sizeKB > 500) {
      const second = await compressImage(file, { maxWidth: 1100, maxHeight: 1100, quality: 0.7 });
      return second.dataUrl;
    }
    return dataUrl;
  };

  // ====== Portfolio handlers ======
  const onPortfolioImageChange = async (id, file) => {
    if (!file) return;
    setUploadingId(`port-${id}`);
    try {
      const url = await uploadImage(file);
      if (!url) return;
      const next = settings.portfolio_items.map(p => p.id === id ? { ...p, image_url: url } : p);
      await saveAll({ ...settings, portfolio_items: next });
    } catch (e) {
      toast.error("Error al subir imagen");
    } finally {
      setUploadingId(null);
    }
  };

  const updatePortfolioField = (id, field, value) => {
    setSettings(s => ({
      ...s,
      portfolio_items: s.portfolio_items.map(p => p.id === id ? { ...p, [field]: value } : p),
    }));
  };

  const deletePortfolioItem = async (id) => {
    if (!window.confirm("¿Eliminar este trabajo del portafolio?")) return;
    const next = settings.portfolio_items.filter(p => p.id !== id);
    await saveAll({ ...settings, portfolio_items: next });
  };

  const addPortfolioItem = async () => {
    const newItem = {
      id: `p${Date.now()}`,
      category: "DISEÑO GRÁFICO",
      label: "Nuevo Trabajo",
      image_url: "",
    };
    await saveAll({ ...settings, portfolio_items: [...settings.portfolio_items, newItem] });
  };

  const movePortfolio = (id, dir) => {
    const items = [...settings.portfolio_items];
    const idx = items.findIndex(p => p.id === id);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    saveAll({ ...settings, portfolio_items: items });
  };

  // ====== Print handlers ======
  const onPrintImageChange = async (id, file) => {
    if (!file) return;
    setUploadingId(`print-${id}`);
    try {
      const url = await uploadImage(file);
      if (!url) return;
      const next = settings.print_items.map(p => p.id === id ? { ...p, image_url: url } : p);
      await saveAll({ ...settings, print_items: next });
    } catch (e) {
      toast.error("Error al subir imagen");
    } finally {
      setUploadingId(null);
    }
  };

  const updatePrintLabel = (id, label) => {
    setSettings(s => ({
      ...s,
      print_items: s.print_items.map(p => p.id === id ? { ...p, label } : p),
    }));
  };

  // ====== Call schedule handlers ======
  const updateSchedule = (field, value) => {
    setSettings(s => ({
      ...s,
      call_schedule: { ...s.call_schedule, [field]: value },
    }));
  };

  const addHoliday = () => {
    if (!newHoliday || !/^\d{4}-\d{2}-\d{2}$/.test(newHoliday)) {
      toast.error("Fecha inválida (formato: YYYY-MM-DD)");
      return;
    }
    const hs = settings.call_schedule.holidays || [];
    if (hs.includes(newHoliday)) {
      toast.error("Esa fecha ya está en la lista");
      return;
    }
    updateSchedule("holidays", [...hs, newHoliday].sort());
    setNewHoliday("");
  };

  const removeHoliday = (date) => {
    updateSchedule("holidays", (settings.call_schedule.holidays || []).filter(h => h !== date));
  };

  if (loading) {
    return (
      <Card className="mb-8">
        <CardContent className="p-8 flex items-center justify-center text-stone-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando configuración...
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* ====== Portfolio gallery management ====== */}
      <Card className="mb-8 border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-blue-50/40" data-testid="portfolio-manager-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-indigo-600" />
              <CardTitle className="text-lg text-indigo-900">Galería de Trabajos (Portafolio)</CardTitle>
            </div>
            <Button
              size="sm"
              onClick={addPortfolioItem}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="add-portfolio-item-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Agregar trabajo
            </Button>
          </div>
          <p className="text-xs text-indigo-800/70 mt-1">
            Agrega, edita o elimina los trabajos visibles en la sección &ldquo;Nuestros Trabajos&rdquo;. Al hacer clic sobre un trabajo en la página, los clientes verán la imagen en grande (lightbox).
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {settings.portfolio_items.map((p, idx) => {
              const isBusy = uploadingId === `port-${p.id}`;
              return (
                <div
                  key={p.id}
                  className="bg-white rounded-xl border border-indigo-200 p-3 shadow-sm"
                  data-testid={`portfolio-item-card-${p.id}`}
                >
                  <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-stone-100 border border-stone-200 flex items-center justify-center mb-2">
                    {p.image_url ? (
                      <img
                        src={resolveImg(p.image_url)}
                        alt={p.label}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center text-stone-400 px-3">
                        <ImageIcon className="w-8 h-8 mx-auto mb-1 opacity-40" />
                        <p className="text-[11px]">Sin imagen — gradient de fallback</p>
                      </div>
                    )}
                    {isBusy && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 text-white animate-spin" />
                      </div>
                    )}
                  </div>
                  <input
                    ref={el => { fileInputs.current[`port-${p.id}`] = el; }}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onPortfolioImageChange(p.id, e.target.files?.[0])}
                  />
                  <div className="space-y-1.5">
                    <select
                      value={p.category || ""}
                      onChange={(e) => updatePortfolioField(p.id, "category", e.target.value)}
                      onBlur={() => saveAll()}
                      className="w-full text-xs border border-stone-300 rounded px-2 py-1"
                      data-testid={`portfolio-cat-${p.id}`}
                    >
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <Input
                      value={p.label || ""}
                      onChange={(e) => updatePortfolioField(p.id, "label", e.target.value)}
                      onBlur={() => saveAll()}
                      placeholder="Título"
                      className="text-xs h-8"
                      data-testid={`portfolio-label-${p.id}`}
                    />
                  </div>
                  <div className="mt-2 flex gap-1">
                    <Button
                      size="sm"
                      onClick={() => fileInputs.current[`port-${p.id}`]?.click()}
                      disabled={isBusy || saving}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8"
                      data-testid={`portfolio-upload-${p.id}`}
                    >
                      <Upload className="w-3.5 h-3.5 mr-1" /> Subir
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => movePortfolio(p.id, -1)}
                      disabled={idx === 0 || saving}
                      className="h-8 px-2 text-xs"
                      title="Mover arriba"
                    >
                      <GripVertical className="w-3.5 h-3.5 rotate-90" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => movePortfolio(p.id, 1)}
                      disabled={idx === settings.portfolio_items.length - 1 || saving}
                      className="h-8 px-2 text-xs"
                      title="Mover abajo"
                    >
                      <GripVertical className="w-3.5 h-3.5 -rotate-90" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deletePortfolioItem(p.id)}
                      disabled={saving}
                      className="h-8 px-2 text-red-600 hover:bg-red-50 text-xs"
                      data-testid={`portfolio-delete-${p.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ====== Print thumbnails management ====== */}
      <Card className="mb-8 border-rose-200 bg-gradient-to-br from-rose-50/60 to-pink-50/40" data-testid="print-manager-card">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-rose-600" />
            <CardTitle className="text-lg text-rose-900">Miniaturas de Impresión Comercial</CardTitle>
          </div>
          <p className="text-xs text-rose-800/70 mt-1">
            Cambia las imágenes de las miniaturas (Brochures, Postcards, etc.) en la sección de Impresión Comercial. Al hacer clic se abre el formulario de contacto.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {settings.print_items.map((p) => {
              const isBusy = uploadingId === `print-${p.id}`;
              return (
                <div
                  key={p.id}
                  className="bg-white rounded-xl border border-rose-200 p-2 shadow-sm"
                  data-testid={`print-item-card-${p.id}`}
                >
                  <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-stone-100 border border-stone-200 flex items-center justify-center mb-2">
                    {p.image_url ? (
                      <img
                        src={resolveImg(p.image_url)}
                        alt={p.label}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center text-stone-400 px-2">
                        <ImageIcon className="w-7 h-7 mx-auto mb-1 opacity-40" />
                        <p className="text-[10px]">Sin imagen</p>
                      </div>
                    )}
                    {isBusy && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                      </div>
                    )}
                  </div>
                  <input
                    ref={el => { fileInputs.current[`print-${p.id}`] = el; }}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onPrintImageChange(p.id, e.target.files?.[0])}
                  />
                  <Input
                    value={p.label || ""}
                    onChange={(e) => updatePrintLabel(p.id, e.target.value)}
                    onBlur={() => saveAll()}
                    className="text-[11px] h-7 mb-1.5"
                    placeholder="Etiqueta"
                    data-testid={`print-label-${p.id}`}
                  />
                  <Button
                    size="sm"
                    onClick={() => fileInputs.current[`print-${p.id}`]?.click()}
                    disabled={isBusy || saving}
                    className="w-full bg-rose-600 hover:bg-rose-700 text-white text-xs h-7"
                    data-testid={`print-upload-${p.id}`}
                  >
                    <Upload className="w-3 h-3 mr-1" /> Cambiar
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ====== Call schedule ====== */}
      <Card className="mb-8 border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-teal-50/40" data-testid="call-schedule-card">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-600" />
            <CardTitle className="text-lg text-emerald-900">Horario de Llamadas Directas</CardTitle>
          </div>
          <p className="text-xs text-emerald-800/70 mt-1">
            Define cuándo el botón &ldquo;LLAMAR AHORA&rdquo; (617-251-1444) está visible. Zona horaria: Massachusetts (Eastern Time). Fuera del horario se oculta automáticamente.
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-emerald-200">
              <div>
                <Label htmlFor="call-enabled" className="font-semibold text-emerald-900">Activar horario</Label>
                <p className="text-xs text-stone-500 mt-0.5">Si está desactivado, el botón nunca se muestra (útil para feriados imprevistos).</p>
              </div>
              <Switch
                id="call-enabled"
                checked={settings.call_schedule.enabled}
                onCheckedChange={(v) => { updateSchedule("enabled", v); saveAll({ ...settings, call_schedule: { ...settings.call_schedule, enabled: v } }); }}
                data-testid="call-schedule-enabled"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="bg-white rounded-lg p-3 border border-emerald-200">
                <Label className="text-xs font-semibold text-emerald-900">Hora de inicio</Label>
                <Input
                  type="time"
                  value={settings.call_schedule.start}
                  onChange={(e) => updateSchedule("start", e.target.value)}
                  onBlur={() => saveAll()}
                  className="mt-1 h-9"
                  data-testid="call-schedule-start"
                />
              </div>
              <div className="bg-white rounded-lg p-3 border border-emerald-200">
                <Label className="text-xs font-semibold text-emerald-900">Hora de fin</Label>
                <Input
                  type="time"
                  value={settings.call_schedule.end}
                  onChange={(e) => updateSchedule("end", e.target.value)}
                  onBlur={() => saveAll()}
                  className="mt-1 h-9"
                  data-testid="call-schedule-end"
                />
              </div>
            </div>

            <div className="bg-white rounded-lg p-3 border border-emerald-200">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-emerald-600" />
                <Label className="font-semibold text-emerald-900">Días feriados / fuera de servicio</Label>
              </div>
              <p className="text-xs text-stone-500 mb-2">Agrega fechas en las que el botón debe ocultarse todo el día.</p>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={newHoliday}
                  onChange={(e) => setNewHoliday(e.target.value)}
                  className="h-9 flex-1"
                  data-testid="call-schedule-new-holiday"
                />
                <Button size="sm" onClick={() => { addHoliday(); saveAll({ ...settings, call_schedule: { ...settings.call_schedule, holidays: [...(settings.call_schedule.holidays || []), newHoliday].filter(Boolean) } }); }} disabled={!newHoliday || saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="call-schedule-add-holiday">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {(settings.call_schedule.holidays || []).length > 0 ? (
                <ul className="mt-3 space-y-1">
                  {settings.call_schedule.holidays.map(h => (
                    <li key={h} className="flex items-center justify-between text-sm bg-emerald-50 rounded px-2 py-1" data-testid={`holiday-${h}`}>
                      <span className="font-mono text-emerald-900">{h}</span>
                      <button
                        type="button"
                        onClick={() => { removeHoliday(h); saveAll({ ...settings, call_schedule: { ...settings.call_schedule, holidays: settings.call_schedule.holidays.filter(x => x !== h) } }); }}
                        className="text-red-600 hover:text-red-700"
                        aria-label={`Eliminar ${h}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-stone-400 mt-2 italic">Sin fechas configuradas</p>
              )}
            </div>

            <div className="flex items-start gap-2 text-xs text-stone-600 bg-amber-50 border border-amber-200 rounded p-2">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span>Cualquier cambio se aplica inmediatamente al refrescar la página pública. El cliente solo ve el botón cuando estás disponible.</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default LandingContentManager;
